import * as moment from 'moment';
import { AmberSidely, BrightRedSidely, CoralSidely, EmeraldSidely, ForestGreenSidely, IndigoSidely, LightCyanSidely, LightLavenderSidely, SteelBlueSidely } from '../../styles/global/css/Utils';
import { translateToString } from '../../styles/global/translate';

// The colour scale of a numeric column on the map: where each colour sits
// along the values, as stops on a bar, and whether the colours blend between
// the stops or hold until the next one. A stop is placed as a share of the
// range the scale spans (the values on the map, or bounds set by hand so
// the thresholds mean the same thing whatever is loaded), or directly at a
// value of the column, the range then running from the first stop to the
// last. Saved with the column (additional column, calculated field or form
// field) in its display options; without one the map keeps its historical
// white to green buckets.

export type ColorStop = {
	// a share of the range (0 at its start, 1 at its end), or a value of the
	// column when the scale's unit is `value`
	at: number,
	// #rrggbb
	color: string,
};

export type ColorScaleKind = 'gradient' | 'steps';

export type ColorScaleUnit = 'percent' | 'value';

export type ColorScale = {
	kind: ColorScaleKind,
	// how `at` is measured; absent = `percent`
	unit?: ColorScaleUnit,
	// in the column's unit (a percentage in %, a money in currency units);
	// null = from the lowest to the highest value on the map. Ignored when
	// the stops are at values: they bound the range themselves.
	range: { min: number, max: number } | null,
	// in any order, two at least
	stops: ColorStop[],
};

// the map colours of a boolean column: one per value
export type BooleanColors = { true: string, false: string };

export type ColumnDisplayOptions = {
	color_scale?: ColorScale,
	boolean_colors?: BooleanColors,
};

// what the map colours of a column type are made of
export type MapColorsKind = 'scale' | 'boolean';

export type ScaleRange = { min: number, max: number };

/** A scale read along its range: the stops as shares of it, 0..1, sorted. */
export type RelativeScale = { kind: ColorScaleKind, range: ScaleRange, stops: ColorStop[] };

// the additional column and form field types a scale applies to: numbers
// of each kind, and dates, read as days from today, negative when gone by
const SCALE_TYPES = new Set<string>(['Integer', 'Number', 'Money', 'ReportColumn', 'Date']);

/** The map colours an additional column, form field or calculated field type takes, if any. */
export function mapColorsKindOf(type: string): MapColorsKind | undefined {
	if (type === 'Boolean') return 'boolean';
	if (SCALE_TYPES.has(type)) return 'scale';
	return undefined;
}

/** A date's value on a scale, in words: 30 days ago, today, in 30 days. */
export function formatDaysFromToday(value: number): string {
	const days = Math.round(Math.abs(value));
	if (days === 0) return translateToString('color_scale.today');
	const key = value < 0 ? (days === 1 ? 'one_day_ago' : 'days_ago') : (days === 1 ? 'in_one_day' : 'in_days');
	return translateToString(`color_scale.${key}`, [['N', days.toString()]]);
}

/** A date's value on a scale: the days from today to it, negative for a date gone by. */
export function daysFromToday(value: string): number {
	return moment.utc(value).diff(moment(), 'days');
}

// the wire carries a percentage as [numer, denom] or {numer, denom}
export function percentOf(percentage: unknown): number {
	if (Array.isArray(percentage)) return (percentage[0] / percentage[1]) * 100;
	const { numer, denom } = percentage as { numer: number, denom: number };
	return (numer / denom) * 100;
}

function toNumber(value: unknown): number | undefined {
	const n = typeof value === 'number' ? value : typeof value === 'string' ? parseFloat(value) : NaN;
	return isNaN(n) ? undefined : n;
}

// a percentage has bounds of its own, which stand in for the values on the
// map when there is no map
export const PERCENT_RANGE: ScaleRange = { min: 0, max: 100 };

/**
 * The number a column's value puts on a scale, in the column's unit (a
 * money in currency units, a date in days from today, a report value by its
 * kind), with the bounds the unit has of its own, if any; nothing when the
 * value has no number.
 */
export function scaleValueOf(type: string, value: unknown): { value: number, natural?: ScaleRange } | undefined {
	if (value === null || value === undefined || value === '' || value === 'empty') return undefined;
	const number = (n: number | undefined, natural?: ScaleRange) => n === undefined ? undefined : { value: n, natural };
	switch (type) {
		case 'Date':
			return typeof value === 'string' && !isNaN(Date.parse(value)) ? { value: daysFromToday(value) } : undefined;
		case 'Money': {
			const n = toNumber(value);
			return number(n === undefined ? undefined : n / 100);
		}
		case 'Integer':
		case 'Number':
			return number(toNumber(value));
		case 'ReportColumn':
		case 'CalculatedField': {
			let v = value;
			if (typeof v === 'string') {
				try { v = JSON.parse(v); } catch (_e) { return undefined; }
			}
			if (!v || typeof v !== 'object') return undefined;
			if ('int' in v) return number(toNumber(v.int));
			if ('float' in v) return number(toNumber(v.float));
			if ('percentage' in v) return number(percentOf(v.percentage), PERCENT_RANGE);
			if ('evolution' in v) {
				const next = (v.evolution as { new?: { val?: unknown } } | undefined)?.new?.val;
				if (next && typeof next === 'object' && 'percentage' in next) return number(percentOf(next.percentage), PERCENT_RANGE);
			}
			return undefined;
		}
	}
	return undefined;
}

/** Whether the scale colours a value by itself, without the values on a map around it. */
export function isAbsolute(scale: ColorScale): boolean {
	return scale.unit === 'value' || scale.range !== null;
}

/**
 * The colour a column's value shows outside the map: a boolean's colour, or
 * the scale's, read over its own stops or bounds when it has them, else
 * over the values shown around the value (a report's rows) when they are
 * given, else over the bounds the value has of its own (a percentage runs
 * 0 to 100). Nothing for a plain number or a date on a scale relative to
 * values that are not at hand.
 */
export function valueColor(options: ColumnDisplayOptions | undefined, type: string, value: unknown, shown?: ScaleRange): string | undefined {
	if (type === 'Boolean') {
		const bool = typeof value === 'boolean' ? value : typeof value === 'string' ? value.toLowerCase() === 'true' : undefined;
		return bool === undefined ? undefined : booleanColorsOf(options)[bool ? 'true' : 'false'];
	}
	const scale = options?.color_scale;
	if (!scale) return undefined;
	const parsed = scaleValueOf(type, value);
	if (!parsed) return undefined;
	const range = isAbsolute(scale) ? ZERO_RANGE : shown ?? parsed.natural;
	return range ? colorOf(scale, parsed.value, range) : undefined;
}

/** The colour of a value shown on its own, no other values around it. */
export function standaloneColor(options: ColumnDisplayOptions | undefined, type: string, value: unknown): string | undefined {
	return valueColor(options, type, value);
}

/** The bounds of the values shown together; nothing without values. */
export function rangeOfValues(values: number[]): ScaleRange | undefined {
	if (values.length === 0) return undefined;
	let min = values[0];
	let max = values[0];
	values.forEach(value => { min = Math.min(min, value); max = Math.max(max, value); });
	return { min, max };
}

// the colours of a boolean when none are set, in the brand's colours
export const DEFAULT_BOOLEAN_COLORS: BooleanColors = { true: EmeraldSidely, false: BrightRedSidely };

export function booleanColorsOf(options: ColumnDisplayOptions | undefined): BooleanColors {
	return { ...DEFAULT_BOOLEAN_COLORS, ...options?.boolean_colors };
}

// an absent bound is open: the bucket also takes everything below / above
export type ScaleBucket = { from?: number, to?: number, color: string };

const WHITE = '#FFFFFF';
const GREEN = ForestGreenSidely;
const ZERO_RANGE: ScaleRange = { min: 0, max: 0 };

// the proposed scales, in the brand's colours; the first is the default
export const COLOR_SCALE_PRESETS: { key: string, stops: ColorStop[] }[] = [
	{ key: 'white_green', stops: [{ at: 0, color: WHITE }, { at: 1, color: ForestGreenSidely }] },
	{ key: 'red_green', stops: [{ at: 0, color: BrightRedSidely }, { at: 0.5, color: AmberSidely }, { at: 1, color: EmeraldSidely }] },
	{ key: 'green_red', stops: [{ at: 0, color: EmeraldSidely }, { at: 0.5, color: AmberSidely }, { at: 1, color: BrightRedSidely }] },
	{ key: 'traffic_light', stops: [{ at: 0, color: BrightRedSidely }, { at: 0.34, color: CoralSidely }, { at: 0.67, color: AmberSidely }, { at: 1, color: EmeraldSidely }] },
	{ key: 'blue', stops: [{ at: 0, color: LightCyanSidely }, { at: 1, color: SteelBlueSidely }] },
	{ key: 'indigo', stops: [{ at: 0, color: LightLavenderSidely }, { at: 1, color: IndigoSidely }] },
];

// the last year, which a date's scale starts over
export const DEFAULT_DATE_SPAN: ScaleRange = { min: -365, max: 0 };

/**
 * The scale a column starts from: the first preset over the values on the
 * map, or, for a date, at values over the last year, the way its stops are
 * best read.
 */
export function defaultColorScale(date = false): ColorScale {
	const stops = COLOR_SCALE_PRESETS[0].stops;
	if (date) {
		const { min, max } = DEFAULT_DATE_SPAN;
		return { kind: 'gradient', unit: 'value', range: null, stops: stops.map(stop => ({ ...stop, at: min + stop.at * (max - min) })) };
	}
	return { kind: 'gradient', range: null, stops: stops.map(stop => ({ ...stop })) };
}

export function sortedStops(stops: ColorStop[]): ColorStop[] {
	return [...stops].sort((a, b) => a.at - b.at);
}

export function clamp01(value: number): number {
	return Math.min(1, Math.max(0, value));
}

function hexToRgb(hex: string): [number, number, number] {
	const raw = hex.replace('#', '');
	const full = raw.length === 3 ? raw.split('').map(c => c + c).join('') : raw;
	const n = parseInt(full, 16);
	if (isNaN(n)) return [0, 0, 0];
	return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function mixHex(a: string, b: string, t: number): string {
	const [r1, g1, b1] = hexToRgb(a);
	const [r2, g2, b2] = hexToRgb(b);
	const channel = (x: number, y: number) => Math.round(x + (y - x) * t);
	return `rgb(${channel(r1, r2)}, ${channel(g1, g2)}, ${channel(b1, b2)})`;
}

/**
 * The range the stops span: from the first stop to the last when they are
 * at values, else the one set by hand, else the values on the map.
 */
export function scaleRange(scale: ColorScale, onMap: ScaleRange): ScaleRange {
	if (scale.unit === 'value' && scale.stops.length > 0) {
		const stops = sortedStops(scale.stops);
		return { min: stops[0].at, max: stops[stops.length - 1].at };
	}
	return scale.range ?? onMap;
}

/** The scale with its stops as shares of the range it spans, sorted. */
export function relativeScale(scale: ColorScale, onMap: ScaleRange = ZERO_RANGE): RelativeScale {
	const stops = sortedStops(scale.stops);
	const range = scaleRange(scale, onMap);
	if (scale.unit !== 'value') return { kind: scale.kind, range, stops };
	const span = range.max - range.min;
	return {
		kind: scale.kind,
		range,
		stops: stops.map((stop, i) => ({ ...stop, at: span > 0 ? (stop.at - range.min) / span : (i === 0 ? 0 : 1) })),
	};
}

/** The colour at `t` (0..1 of the range) of a scale read along its range. */
export function colorAt(scale: Pick<RelativeScale, 'kind' | 'stops'>, t: number): string {
	const stops = scale.stops;
	if (stops.length === 0) return GREEN;
	const x = clamp01(t);
	if (x <= stops[0].at) return stops[0].color;
	const last = stops[stops.length - 1];
	if (x >= last.at) return last.color;
	let i = 1;
	while (i < stops.length - 1 && x >= stops[i].at) i++;
	const lower = stops[i - 1];
	const upper = stops[i];
	if (scale.kind === 'steps') return lower.color;
	const span = upper.at - lower.at;
	return span <= 0 ? upper.color : mixHex(lower.color, upper.color, (x - lower.at) / span);
}

/** Where a value sits in the range, 0..1. A flat range puts everything at the end. */
export function positionOf(value: number, range: ScaleRange): number {
	if (range.max <= range.min) return 1;
	return clamp01((value - range.min) / (range.max - range.min));
}

export function colorOf(scale: ColorScale, value: number, onMap: ScaleRange): string {
	const relative = relativeScale(scale, onMap);
	return colorAt(relative, positionOf(value, relative.range));
}

/**
 * The value intervals the legend and the cluster pies list, each with its
 * colour. Steps are thresholds: each stop starts its colour, which holds
 * until the next stop; the first colour also covers everything below and
 * the last everything above. A gradient is cut in `count` equal slices.
 */
export function scaleBuckets(scale: ColorScale, onMap: ScaleRange, count = 10): ScaleBucket[] {
	const relative = relativeScale(scale, onMap);
	const { range, stops } = relative;
	const valueAt = (t: number) => range.min + t * (range.max - range.min);
	if (scale.kind === 'steps') {
		return stops.map((stop, i) => ({
			from: i === 0 ? undefined : valueAt(stop.at),
			to: i === stops.length - 1 ? undefined : valueAt(stops[i + 1].at),
			color: stop.color,
		}));
	}
	return Array.from({ length: count }, (_, i) => ({
		from: valueAt(i / count),
		to: valueAt((i + 1) / count),
		color: colorAt(relative, (i + 0.5) / count),
	}));
}

/** The bucket holding a value: the last one when it is at or past the end. */
export function bucketOf(buckets: ScaleBucket[], value: number): ScaleBucket {
	return buckets.find((bucket, i) => bucket.to === undefined || value < bucket.to || i === buckets.length - 1) ?? buckets[buckets.length - 1];
}

/**
 * What the legend and the pies call a bucket: its bounds, in the column's
 * unit; between moments, before and after, when the column is a date.
 */
export function scaleBucketLabel(bucket: ScaleBucket, format: (value: number) => string, dates = false): string {
	if (bucket.from === undefined && bucket.to === undefined) return translateToString('all');
	if (bucket.from === undefined) return translateToString(dates ? 'color_scale.before' : 'color_scale.below', [['NAME', format(bucket.to!)]]);
	if (bucket.to === undefined) return translateToString(dates ? 'color_scale.and_after' : 'color_scale.and_above', [['NAME', format(bucket.from)]]);
	return translateToString(dates ? 'color_scale.between' : 'from_{{NAME1}}_to_{{NAME2}}', [['NAME1', format(bucket.from)], ['NAME2', format(bucket.to)]]);
}

/** The bar the editor and the legend draw. */
export function cssGradient(scale: ColorScale): string {
	const { kind, stops } = relativeScale(scale);
	if (stops.length === 0) return GREEN;
	if (stops.length === 1) return stops[0].color;
	const pct = (at: number) => `${(clamp01(at) * 100).toFixed(1)}%`;
	if (kind === 'steps') {
		const parts: string[] = [];
		stops.forEach((stop, i) => {
			const next = stops[i + 1];
			parts.push(`${stop.color} ${pct(stop.at)}`);
			parts.push(`${stop.color} ${pct(next ? next.at : 1)}`);
		});
		return `linear-gradient(to right, ${stops[0].color} 0%, ${parts.join(', ')})`;
	}
	return `linear-gradient(to right, ${stops.map(stop => `${stop.color} ${pct(stop.at)}`).join(', ')})`;
}

export function isValidHex(color: string): boolean {
	return /^#[0-9a-fA-F]{6}$/.test(color);
}

/** The preset the stops came from, if they still match one. */
export function presetOf(scale: ColorScale): string | undefined {
	const stops = relativeScale(scale).stops;
	return COLOR_SCALE_PRESETS.find(preset =>
		preset.stops.length === stops.length
		&& preset.stops.every((stop, i) => Math.abs(stop.at - stops[i].at) < 1e-6 && stop.color.toUpperCase() === stops[i].color.toUpperCase())
	)?.key;
}
